import React from "react";

import { css } from "@emotion/css";
import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { Contact } from "@/models/ContactModel.ts";
import { ContactData, contactFormSchema } from "@/pages/patient-medical-file/contact-patient/ContactPatient.schema.ts";
import {
  CommentInput,
  EmailInput,
  EmergencyContactInput,
  FirstnameInput,
  LastnameInput,
  PhoneInput,
  SelectRelationshipInput,
} from "@/pages/patient-medical-file/contact-patient/ContactPatientFormInputs.tsx";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { Form } from "@/uiKit/atoms/forms/Form.tsx";
import { Loader } from "@/uiKit/atoms/Loader.tsx";

export type ContactPatientFormProps = {
  initialValues?: Contact;
  isSubmitting: boolean;
  onSubmit: (data: ContactData) => void;
  submitText: string;
  cancel: () => void;
};

const FORM_ID = "ContactPatientForm";

export const ContactPatientForm: React.FC<ContactPatientFormProps> = ({
  initialValues,
  isSubmitting,
  onSubmit,
  submitText,
  cancel,
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  const formContext = useForm<ContactData>({
    defaultValues: initialValues || {
      contactType: "unknown",
      isEmergencyContact: false,
    },
    resolver: zodResolver(contactFormSchema) as Resolver<ContactData>,
  });

  return (
    <Card className={styles.container}>
      <Form id={FORM_ID} onSubmit={onSubmit} formContext={formContext}>
        <CardContent className={styles.form}>
          <SelectRelationshipInput className={styles.selectRelationship} />
          <div className={styles.row}>
            <FirstnameInput className={styles.input} />
            <LastnameInput className={styles.input} />
          </div>
          <div className={styles.row}>
            <PhoneInput className={styles.input} />
            <EmailInput className={styles.input} />
          </div>
          <CommentInput className={styles.comment} />
          <div className={styles.row}>
            <EmergencyContactInput className={styles.isEmergencyContact} />
          </div>
        </CardContent>
        <CardFooter>
          <Button
            data-action="medical-file.contact.cancel"
            id="cancel-contact-create"
            onClick={cancel}
            variant="outlined"
            disabled={isSubmitting}
          >
            {t("button.cancel")}
          </Button>
          <Button
            data-action="medical-file.contact.create"
            id="submit-contact-create"
            type="submit"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <Loader size="S" /> : submitText}
          </Button>
        </CardFooter>
      </Form>
    </Card>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css``,
  form: css`
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: ${theme.spacing(8)};
    flex-wrap: wrap;
  `,
  selectRelationship: css`
    flex: 1 1;
    min-width: ${theme.spacing(160)};
  `,
  comment: css`
    width: 100%;
  `,
  row: css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(8)};
    width: 100%;
    flex-wrap: wrap;
  `,
  input: css`
    flex: 1 1 ${theme.spacing(96)};
  `,
  isEmergencyContact: css`
    flex-shrink: 1;
    width: auto;
  `,
});
